import React, { useEffect, useState } from 'react';
import InventoryUnitScanner from './InventoryUnitScanner';
import RestockAdmin from './RestockAdmin';
import SeasonCloseout from './SeasonCloseout';
import PreseasonPlanner from './PreseasonPlanner';
import { getMemberAccessToken } from '../auth/supabase';
import './inventory.css';

function AdminHomeButton() {
  return <button type="button" className="inventory-save" onClick={() => window.location.assign('/admin')}>Admin Home</button>;
}

function AdminSwitcher({ section, setSection }) {
  return (
    <nav className="inventory-admin-switcher" aria-label="Private inventory sections">
      <button type="button" onClick={() => window.location.assign('/admin')}>Admin Home</button>
      <button type="button" className={section === 'scanner' ? 'active' : ''} onClick={() => setSection('scanner')}>Scanner</button>
      <button type="button" className={section === 'restock' ? 'active' : ''} onClick={() => setSection('restock')}>Restock Requests</button>
      <button type="button" className={section === 'preseason' ? 'active' : ''} onClick={() => setSection('preseason')}>Preseason</button>
      <button type="button" className={section === 'inventory' ? 'active' : ''} onClick={() => setSection('inventory')}>Seasonal Inventory</button>
    </nav>
  );
}

export default function InventoryAdminProtected({ member }) {
  const [status, setStatus] = useState('checking');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [section, setSection] = useState('scanner');

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (member && !member.is_admin) {
        if (!cancelled) setStatus('denied');
        return;
      }

      if (member?.is_admin) {
        try {
          const token = await getMemberAccessToken({ timeoutMs: 4000 });
          if (!token) throw new Error('Member session could not be read. Refresh the page or return to Admin Home and try again.');
          const response = await fetch('/api/member-tracking?mode=inventory-admin-session', {
            method: 'POST',
            cache: 'no-store',
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.ok) {
            if (!cancelled) setStatus('authenticated');
            return;
          }
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload?.error || 'Unable to establish inventory admin session.');
        } catch (err) {
          if (!cancelled) setError(err?.message || 'Unable to establish inventory admin session.');
        }
      }

      fetch('/api/tracking?admin=session', { cache: 'no-store' })
        .then(response => { if (!cancelled) setStatus(response.ok ? 'authenticated' : 'login'); })
        .catch(() => { if (!cancelled) setStatus('login'); });
    };
    check();
    return () => { cancelled = true; };
  }, [member]);

  const submit = async event => {
    event.preventDefault();
    setError('');
    setStatus('signing-in');
    try {
      const response = await fetch('/api/tracking?admin=login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Unable to sign in.');
      setPassword('');
      setStatus('authenticated');
    } catch (err) {
      setError(err?.message || 'Unable to sign in.');
      setStatus('login');
    }
  };

  if (status === 'authenticated') {
    return (
      <>
        <AdminSwitcher section={section} setSection={setSection} />
        {section === 'scanner' && <InventoryUnitScanner />}
        {section === 'restock' && (
          <div className="inventory-root">
            <header className="inventory-topbar">
              <div><h1>Line Medic Restock Requests</h1><p>Return usage, approval, Logistics ordering, and restock status</p></div>
              <span>Private admin</span>
            </header>
            <main className="inventory-page"><RestockAdmin /></main>
          </div>
        )}
        {section === 'preseason' && <PreseasonPlanner />}
        {section === 'inventory' && <SeasonCloseout />}
      </>
    );
  }

  if (status === 'denied') {
    return (
      <div className="inventory-root">
        <header className="inventory-topbar">
          <div><h1>Line Medic Inventory Admin</h1><p>Private program administration</p></div>
          <AdminHomeButton />
        </header>
        <main className="inventory-page"><section className="inventory-card inventory-login"><h2>Admin access required</h2><p>Your member account does not have program-admin access.</p></section></main>
      </div>
    );
  }

  return (
    <div className="inventory-root">
      <header className="inventory-topbar">
        <div><h1>Line Medic Inventory Admin</h1><p>Private inventory scanning and seasonal management</p></div>
        <AdminHomeButton />
      </header>
      <main className="inventory-page">
        <section className="inventory-card inventory-login">
          <h2>{status === 'checking' ? 'Checking admin session…' : 'Inventory Admin Sign In'}</h2>
          {status === 'checking' ? <p>One moment.</p> : (
            <form className="inventory-login-form" onSubmit={submit}>
              {error && <div className="inventory-error">{error}</div>}
              <label>Admin password
                <input type="password" value={password} onChange={event => setPassword(event.target.value)} autoComplete="current-password" autoFocus required />
              </label>
              <button type="submit" disabled={status === 'signing-in'}>{status === 'signing-in' ? 'Signing in…' : 'Open Inventory Admin'}</button>
            </form>
          )}
        </section>
      </main>
    </div>
  );
}
